/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React from "react";

function formatCount(value) {
  return typeof value === "number" ? value.toLocaleString() : "Not estimated";
}

function formatChange(value) {
  if (typeof value !== "number") return "Not estimated";
  return value > 0 ? `+${value.toLocaleString()}` : value.toLocaleString();
}

function StatTile({ label, value, detail }) {
  return (
    <div className="rounded-xl border border-[#93CA87]/35 bg-white p-3 text-center">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#374049]/55">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-[#01767A]">{formatCount(value)}</p>
      {detail && <p className="mt-1 text-[10px] text-[#374049]/60">{detail}</p>}
    </div>
  );
}

export default function TaxonomicImpactPreview({ report, onClose, onRefresh, refreshing }) {
  const totals = report.summary;
  const stats = report.statistics;
  const generatedAt = new Date(report.generatedAt);

  return (
    <section className="mt-5 space-y-5 rounded-2xl border border-[#24C3BE]/45 bg-[#FCFFF6] p-4 shadow-sm sm:p-5" aria-label="Taxonomic impact preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#01767A]">Read-only analysis · Saved draft v{report.revisionVersion}</p>
          <h3 className="mt-1 text-2xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>Taxonomic impact preview</h3>
          <p className="mt-1 text-xs text-[#374049]/60">Generated {Number.isNaN(generatedAt.getTime()) ? report.generatedAt : generatedAt.toLocaleString()} · {formatCount(totals.scannedSpeciesDocuments)} species documents scanned</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onRefresh} disabled={refreshing} className="rounded-lg bg-[#01767A] px-3 py-2 text-xs font-semibold text-white hover:bg-[#374049] disabled:opacity-50">{refreshing ? "Checking..." : "Refresh preview"}</button>
          <button type="button" onClick={onClose} className="rounded-lg border border-[#374049]/20 bg-white px-3 py-2 text-xs font-semibold text-[#374049] hover:bg-slate-50">Close</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Users" value={totals.affectedUsers} />
        <StatTile label="Species records" value={totals.affectedSpeciesDocuments} />
        <StatTile label="Photographs" value={totals.affectedPhotographs} />
        <StatTile label="Collections" value={totals.affectedCollections} detail="Distinct user collections" />
        <StatTile label="Collection assignments" value={totals.affectedCollectionMemberships} />
        <StatTile label="Existing destinations" value={totals.existingDestinationUsers} detail="Users with target already present" />
      </div>

      {totals.affectedSpeciesDocuments === 0 && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">No currently stored species records match this draft's original scientific name(s). The revision may still update the suggestion catalog later.</p>
      )}

      <div className="overflow-x-auto rounded-xl border border-[#93CA87]/40 bg-white">
        <h4 className="px-4 py-3 text-sm font-semibold text-[#374049]">Affected source species</h4>
        <table className="w-full min-w-[540px] text-left text-xs text-[#374049]">
          <thead className="border-y border-[#93CA87]/30 bg-[#FCFFF6] text-[10px] uppercase tracking-wide text-[#374049]/65">
            <tr><th className="p-3">Scientific name</th><th className="p-3 text-right">Users</th><th className="p-3 text-right">Records</th><th className="p-3 text-right">Photos</th><th className="p-3 text-right">Collection assignments</th></tr>
          </thead>
          <tbody className="divide-y divide-[#93CA87]/20">
            {report.sources.map((source) => (
              <tr key={source.scientificName}>
                <td className="p-3 italic">{source.scientificName}</td><td className="p-3 text-right">{formatCount(source.affectedUsers)}</td><td className="p-3 text-right">{formatCount(source.speciesDocuments)}</td><td className="p-3 text-right">{formatCount(source.photographs)}</td><td className="p-3 text-right">{formatCount(source.collectionMemberships)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-[#93CA87]/40 bg-white p-4">
        <h4 className="text-sm font-semibold text-[#374049]">Estimated effects on statistics</h4>
        <p className="mt-1 text-xs leading-5 text-[#374049]/60">{stats.predictable ? "Projections assume the proposed automatic migration is completed and that no other observations change." : report.revisionType === "species_split" ? "This is a species split: final counts depend on each user's photograph-by-photograph identification." : "Some affected taxon records have inconsistent stored names; projections are withheld until they are reconciled."}</p>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div className="rounded-lg bg-[#FCFFF6] p-3">
            <p className="text-[11px] font-semibold text-[#374049]">Species across affected user archives</p>
            <p className="mt-1 text-xs text-[#374049]/70">Current: {formatCount(stats.affectedUsersCurrentSpecies)}</p>
            <p className="text-xs text-[#374049]/70">Projected: {formatCount(stats.affectedUsersProjectedSpecies)}</p>
            <p className="text-xs font-semibold text-[#01767A]">Change: {formatChange(stats.affectedUsersSpeciesChange)}</p>
          </div>
          <div className="rounded-lg bg-[#FCFFF6] p-3">
            <p className="text-[11px] font-semibold text-[#374049]">Genera across affected user archives</p>
            <p className="mt-1 text-xs text-[#374049]/70">Current: {formatCount(stats.affectedUsersCurrentGenera)}</p>
            <p className="text-xs text-[#374049]/70">Projected: {formatCount(stats.affectedUsersProjectedGenera)}</p>
            <p className="text-xs font-semibold text-[#01767A]">Change: {formatChange(stats.affectedUsersGenusChange)}</p>
          </div>
          <div className="rounded-lg bg-[#FCFFF6] p-3">
            <p className="text-[11px] font-semibold text-[#374049]">Community unique species</p>
            <p className="mt-1 text-xs text-[#374049]/70">Current: {formatCount(stats.communityCurrentUniqueSpecies)}</p>
            <p className="text-xs text-[#374049]/70">Projected: {formatCount(stats.communityProjectedUniqueSpecies)}</p>
            <p className="text-xs font-semibold text-[#01767A]">Change: {formatChange(stats.communitySpeciesChange)}</p>
          </div>
        </div>
        <p className="mt-3 text-xs text-[#374049]/65">Community photographs: {formatCount(stats.communityCurrentPhotographs)}. Name changes and transfers do not create or delete photographs.</p>
      </div>

      <details className="rounded-xl border border-[#93CA87]/40 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-[#374049]">Affected users ({formatCount(totals.affectedUsers)})</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[700px] text-xs text-[#374049]">
            <thead className="border-b border-[#93CA87]/30 bg-[#FCFFF6] text-[10px] uppercase tracking-wide text-[#374049]/65"><tr><th className="p-2 text-left">User ID</th><th className="p-2 text-right">Records</th><th className="p-2 text-right">Photos</th><th className="p-2 text-right">Collections</th><th className="p-2 text-right">Species change</th><th className="p-2 text-right">Genera change</th><th className="p-2 text-left">Destination exists</th></tr></thead>
            <tbody className="divide-y divide-[#93CA87]/20">
              {report.users.map((user) => <tr key={user.uid}><td className="p-2 font-mono text-[10px]">{user.uid}</td><td className="p-2 text-right">{user.speciesDocuments}</td><td className="p-2 text-right">{user.photographs}</td><td className="p-2 text-right">{user.affectedCollections}</td><td className="p-2 text-right">{formatChange(user.speciesChange)}</td><td className="p-2 text-right">{formatChange(user.genusChange)}</td><td className="p-2">{user.destinationAlreadyExists ? "Yes" : "No"}</td></tr>)}
            </tbody>
          </table>
          {report.usersTruncated > 0 && <p className="mt-2 text-xs text-[#374049]/60">Showing the first {report.users.length} users; {formatCount(report.usersTruncated)} additional affected users are included in totals.</p>}
        </div>
      </details>

      <details className="rounded-xl border border-[#93CA87]/40 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-[#374049]">Affected collections ({formatCount(totals.affectedCollections)})</summary>
        <div className="mt-3 max-h-60 overflow-y-auto">
          {report.collections.length === 0 ? <p className="text-xs text-[#374049]/60">No collection assignments found for the affected photographs.</p> : report.collections.map((entry) => (
            <div key={`${entry.uid}-${entry.name}`} className="flex flex-wrap justify-between gap-2 border-b border-[#93CA87]/20 py-2 text-xs text-[#374049]">
              <span className="break-words"><strong>{entry.name}</strong> <span className="font-mono text-[10px] text-[#374049]/50">({entry.uid})</span></span>
              <span className="font-semibold">{formatCount(entry.photographs)} photos</span>
            </div>
          ))}
          {report.collectionsTruncated > 0 && <p className="mt-2 text-xs text-[#374049]/60">{formatCount(report.collectionsTruncated)} additional collections are included in totals.</p>}
        </div>
      </details>

      <div className="rounded-xl border border-[#93CA87]/40 bg-white px-4 py-3 text-xs leading-5 text-[#374049]/65">
        <p className="font-semibold text-[#374049]">Preview limitations</p>
        <ul className="mt-1 list-disc space-y-1 pl-5">{report.warnings.map((note, index) => <li key={index}>{note}</li>)}</ul>
        <p className="mt-3 font-medium text-[#01767A]">No observations, taxonomic files, or Firestore revision records were modified by this preview.</p>
      </div>
    </section>
  );
}
